import { useMemo } from "react";
import { useSearchParams } from "react-router";

import Logo from "@/components/logo";
import { UILink } from "@/components/ui-link";
import { paths } from "@/config/paths";
import { useUseCases } from "@/features/sign-up/api/get-use-cases";
import { useSignUpCountries } from "@blueskydataplatform/shared-ui/features/auth/api/get-countries";
import { Button } from "@blueskydataplatform/shared-ui/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@blueskydataplatform/shared-ui/components/ui/card";
import { Skeleton } from "@blueskydataplatform/shared-ui/components/ui/skeleton";
import {
  normalizeSignUpGeoRestriction,
  resolveSignUpGeoNewsletterSubscribeUrl,
  resolveSignUpGeoRestrictionBannerHtml,
} from "@blueskydataplatform/shared-ui/utils/sign-up-geo-restriction";
const RegionUnavailable = () => {
  const [searchParams] = useSearchParams();
  const countryCode = searchParams.get("country")?.trim().toUpperCase() ?? "";
  const { data, isLoading } = useUseCases();
  const { countriesMap } = useSignUpCountries();

  const geoRestriction = useMemo(() => normalizeSignUpGeoRestriction(data?.geoRestriction), [data?.geoRestriction]);

  const countryLabel = useMemo(() => {
    if (!countryCode) return null;
    const entry = countriesMap?.[countryCode];
    if (!entry) return countryCode;
    return Array.isArray(entry) ? entry[0] : entry;
  }, [countryCode, countriesMap]);

  const bannerHtml = resolveSignUpGeoRestrictionBannerHtml(geoRestriction.bannerHtml);

  const newsletterSubscribeUrl = resolveSignUpGeoNewsletterSubscribeUrl(geoRestriction.newsletter.subscribeUrl);
  const showNewsletterAction = geoRestriction.newsletter.enabled && Boolean(newsletterSubscribeUrl);

  const handleNewsletterSubscribe = () => {
    if (!newsletterSubscribeUrl) return;
    window.location.href = newsletterSubscribeUrl;
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-2xl shadow-sm">
        <CardHeader className="space-y-3">
          <div className="flex justify-center">
            <Logo className="mb-4" />
          </div>
          <CardTitle className="text-2xl font-bold text-center">Not available in your region</CardTitle>
          <CardDescription className="text-center max-w-md mx-auto">
            {isLoading ? (
              <Skeleton className="mx-auto h-4 w-64" />
            ) : countryLabel ? (
              <>Registration is not available for users in {countryLabel}.</>
            ) : (
              <>Registration is not available in your region.</>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : bannerHtml ? (
            <div
              className="text-muted-foreground text-sm leading-relaxed [&_a]:text-info [&_a]:underline [&_p+p]:mt-3"
              dangerouslySetInnerHTML={{ __html: bannerHtml }}
            />
          ) : (
            ""
          )}

          {showNewsletterAction && (
            <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
              {geoRestriction.newsletter.labelHtml ? (
                <div
                  className="text-muted-foreground text-sm leading-relaxed [&_a]:text-info [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: geoRestriction.newsletter.labelHtml }}
                />
              ) : (
                <p className="text-muted-foreground text-sm leading-relaxed">
                  Stay informed about product availability in your region.
                </p>
              )}
              <div className="flex justify-center">
                <Button type="button" className="w-full sm:w-auto" onClick={handleNewsletterSubscribe}>
                  {geoRestriction.newsletter.buttonLabel}
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-center">
            <UILink to={paths.home.getHref()} className="text-sm" target="_self">
              Return to Sign In
            </UILink>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegionUnavailable;
